/**
 * API client for VI API Server.
 * Handles auth headers and base URL configuration.
 */

// In dev mode with Vite proxy, use relative URLs (proxy handles /api → localhost:8000)
// In production, use the configured API URL (build-time or runtime)
const API_URL = import.meta.env.VITE_API_URL || window.__RUNTIME_API_URL__ || '';

// --- Content-hash upload cache (issue #999) ---
//
// Stores (sha256(blob) → S3 public_url) in localStorage so that re-attaching
// the same file in /playground/chat skips the presign + PUT round-trip.
//
// Always-on in dev and prod (no feature flag). Metadata only — we never store
// the blob itself, so ~200 entries × ~200 bytes ≈ 40KB stays well under the
// ~5MB localStorage quota.
//
// Invalidation strategy: **30-day TTL**. Entries older than 30 days are
// treated as a miss and re-uploaded. This self-heals deleted S3 objects
// within a month without the per-hit round-trip cost of a HEAD probe.
// (Chose TTL over lazy HEAD because it requires zero network traffic on hit.)
//
// Eviction: when the entry count exceeds MAX_ENTRIES, drop the oldest by
// created_at. Access-time LRU isn't worth the write amplification at N=200.
const UPLOAD_CACHE_KEY = 'vi:upload-cache:v1';
const UPLOAD_CACHE_MAX_ENTRIES = 200;
const UPLOAD_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Parse a presigned S3 URL's absolute expiry (epoch ms). Returns null when the
 * URL isn't an AWS SigV4 presigned GET (e.g. a permanent CDN URL), so callers
 * can fall back to their own TTL logic.
 *
 * The signature is bound to ``X-Amz-Date`` (ISO8601 basic: YYYYMMDDTHHMMSSZ) +
 * ``X-Amz-Expires`` (seconds, max 604800 = 7d for IAM-user-signed URLs). Past
 * that point S3 returns 403, even if the underlying object is still there.
 */
export function parsePresignedExpiryMs(url) {
  try {
    const u = new URL(url);
    const date = u.searchParams.get('X-Amz-Date');
    const expires = u.searchParams.get('X-Amz-Expires');
    if (!date || !expires) return null;
    const m = date.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    if (!m) return null;
    const [, y, mo, d, h, mi, s] = m;
    const epochMs = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
    return epochMs + Number(expires) * 1000;
  } catch {
    return null;
  }
}

/** Compute SHA-256 hex digest of a Blob. */
export async function hashBlob(blob) {
  // Prefer Blob.arrayBuffer() where available; fall back to Response wrapper
  // for environments (e.g. older jsdom) whose Blob lacks the method.
  const buf = typeof blob.arrayBuffer === 'function'
    ? await blob.arrayBuffer()
    : await new Response(blob).arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

export class UploadCache {
  constructor({
    storage = (typeof localStorage !== 'undefined' ? localStorage : null),
    key = UPLOAD_CACHE_KEY,
    maxEntries = UPLOAD_CACHE_MAX_ENTRIES,
    ttlMs = UPLOAD_CACHE_TTL_MS,
    now = () => Date.now(),
  } = {}) {
    this.storage = storage;
    this.key = key;
    this.maxEntries = maxEntries;
    this.ttlMs = ttlMs;
    this.now = now;
  }

  _read() {
    if (!this.storage) return {};
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch {
      return {};
    }
  }

  _write(entries) {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.key, JSON.stringify(entries));
    } catch (err) {
      // Quota exceeded or storage disabled — drop the cache rather than throw.
      console.warn('[upload-cache] write failed:', err);
      try { this.storage.removeItem(this.key); } catch { /* ignore */ }
    }
  }

  /** Return cached entry for hash, or null if missing/expired.
   *
   * Two staggered expiries apply:
   *   1. Local TTL (default 30d) — bounds localStorage growth.
   *   2. Presigned URL expiry — parsed from X-Amz-Date + X-Amz-Expires on the
   *      stored ``public_url``. AWS SigV4 caps at 7d, so a 30d TTL alone would
   *      keep serving stale URLs that S3 then rejects with 403. We treat the
   *      entry as a miss within a 60s safety margin of the URL's expiry.
   */
  get(hash) {
    const entries = this._read();
    const entry = entries[hash];
    if (!entry) return null;
    const drop = () => { delete entries[hash]; this._write(entries); };
    if (this.now() - (entry.created_at || 0) > this.ttlMs) {
      drop();
      return null;
    }
    if (entry.public_url) {
      const expiryMs = parsePresignedExpiryMs(entry.public_url);
      if (expiryMs !== null && expiryMs - this.now() < 60 * 1000) {
        drop();
        return null;
      }
    }
    return entry;
  }

  /** Store an entry by hash; evicts the oldest if over capacity. */
  set(hash, entry) {
    const entries = this._read();
    entries[hash] = { ...entry, created_at: this.now() };
    const keys = Object.keys(entries);
    if (keys.length > this.maxEntries) {
      // Drop oldest by created_at until we're back under the cap.
      keys.sort((a, b) => (entries[a].created_at || 0) - (entries[b].created_at || 0));
      const overflow = keys.length - this.maxEntries;
      for (let i = 0; i < overflow; i++) delete entries[keys[i]];
    }
    this._write(entries);
  }

  clear() {
    if (!this.storage) return;
    try { this.storage.removeItem(this.key); } catch { /* ignore */ }
  }

  list() {
    return this._read();
  }

  size() {
    return Object.keys(this._read()).length;
  }
}

const ACCESS_TOKEN_KEY = 'uva_access_token';
const REFRESH_TOKEN_KEY = 'uva_refresh_token';
// Stage 8: default bound api key stored separately from JWT so business calls
// can use sk-vi-* (mirroring production auth path) while management endpoints
// can still fall back to JWT.
const API_KEY_KEY = 'uva_default_api_key';

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  getApiKey: () => localStorage.getItem(API_KEY_KEY),
  setAccess: (t) => {
    if (t) localStorage.setItem(ACCESS_TOKEN_KEY, t);
    else localStorage.removeItem(ACCESS_TOKEN_KEY);
  },
  setRefresh: (t) => {
    if (t) localStorage.setItem(REFRESH_TOKEN_KEY, t);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
  setApiKey: (k) => {
    if (k) localStorage.setItem(API_KEY_KEY, k);
    else localStorage.removeItem(API_KEY_KEY);
  },
  clearApiKey: () => localStorage.removeItem(API_KEY_KEY),
  set: (access, refresh) => {
    if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(API_KEY_KEY);
  },
};

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
    // viUserId is set by useAuth after successful login (api.setViUserId)
    this._viUserId = localStorage.getItem('vi-user-id') || null;
    // Content-hash upload cache (issue #999) — always-on in dev and prod.
    this.uploadCache = new UploadCache();
  }

  // --- Device ID management ---

  getDeviceId() {
    let deviceId = localStorage.getItem('vi-device-id');
    if (!deviceId) {
      const uuid = crypto.randomUUID();
      deviceId = 'dev-' + uuid.replace(/-/g, '').slice(0, 16);
      localStorage.setItem('vi-device-id', deviceId);
    }
    return deviceId;
  }

  getViUserId() {
    return this._viUserId;
  }

  setViUserId(id) {
    this._viUserId = id;
    if (id) {
      localStorage.setItem('vi-user-id', id);
    } else {
      localStorage.removeItem('vi-user-id');
    }
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  /**
   * Build the Authorization header for an outbound request.
   *
   * Stage 8: business calls default to ``preferApiKey: true`` so they ship
   * ``sk-vi-*`` (1:1 with prod); management calls (auth/keys/cards CRUD)
   * pass ``preferApiKey: false`` to use JWT. Both paths fall back to the
   * other token when the preferred one isn't in localStorage.
   */
  async getAuthHeaders({ preferApiKey = true } = {}) {
    const headers = {};
    const apiKey = tokenStore.getApiKey();
    const access = tokenStore.getAccess();
    const primary = preferApiKey ? apiKey : access;
    const fallback = preferApiKey ? access : apiKey;
    const token = primary || fallback;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      headers['X-Device-Id'] = this.getDeviceId();
    }
    return headers;
  }

  async request(path, options = {}, { retries = 2, backoff = 500, timeout = 5000, preferApiKey = true } = {}) {
    const headers = {
      // Skip Content-Type for FormData — the browser sets it with the
      // multipart boundary automatically.
      ...(options.rawBody ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };

    const apiKey = tokenStore.getApiKey();
    const access = tokenStore.getAccess();
    const primary = preferApiKey ? apiKey : access;
    const fallback = preferApiKey ? access : apiKey;
    const token = primary || fallback;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      headers['X-Device-Id'] = this.getDeviceId();
    }

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        const response = await fetch(`${this.baseUrl}${path}`, {
          ...options,
          headers,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.status === 401) {
          throw new Error('Unauthorized');
        }

        // Budget exhausted — show modal, don't retry
        if (response.status === 429) {
          const data = await response.json().catch(() => ({}));
          const detail = (typeof data.detail === 'object' && data.detail) ? data.detail : {};
          if (detail.code === 'budget_error') {
            window.dispatchEvent(new CustomEvent('uva:budget_error', {
              detail: { message: detail.message, reset_at: detail.reset_at },
            }));
            const err = new Error(detail.message || 'API key budget exhausted');
            err.code = 'budget_error';
            err.reset_at = detail.reset_at;
            throw err;
          }
          throw new Error(data.detail || `Rate limited: 429`);
        }

        // Don't retry client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `Request failed: ${response.status}`);
        }

        // Retry server errors (5xx)
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const text = await response.text();
        return text ? JSON.parse(text) : null;
      } catch (err) {
        lastError = err;
        // Don't retry auth errors or client errors
        if (err.message === 'Unauthorized' || err.message.startsWith('Request failed:')) {
          throw err;
        }
        // Retry on network errors and server errors
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, backoff * Math.pow(2, attempt)));
          continue;
        }
      }
    }
    throw lastError;
  }

  async getMe() {
    return this.request('/api/auth/me');
  }

  /**
   * Mint an HttpOnly session cookie so browser EventSource (which cannot set
   * custom headers) can authenticate the global SSE stream at
   * ``GET /api/users/events``. Must be called after login and on every
   * bootstrap where we already have a JWT.
   */
  async createSession() {
    const headers = { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) };
    const resp = await fetch(`${this.baseUrl}/api/auth/session`, {
      method: 'POST',
      headers,
      credentials: 'include',
    });
    if (!resp.ok) throw new Error(`session mint failed: ${resp.status}`);
    return resp.json();
  }

  // LiveKit endpoints
  async getLiveKitToken() {
    return this.request('/api/livekit/token', { method: 'POST' });
  }

  async getAnonymousLiveKitToken() {
    const deviceId = this.getDeviceId();
    const data = await this.request('/api/livekit/anonymous', {
      method: 'POST',
      body: JSON.stringify({ device_id: deviceId }),
    });
    this.setViUserId(data.vi_user_id);
    return data;
  }

  // Session endpoints

  /**
   * Create a new session. Returns { sessionId }.
   * Call this before interacting via the session stream endpoint.
   */
  async createSession({ mediaUrls, context } = {}) {
    const body = {};
    if (mediaUrls) body.media_urls = mediaUrls;
    if (context) body.context = context;
    return this.request('/api/users/sessions', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getSessions({ summary = false, format } = {}) {
    const params = new URLSearchParams();
    if (summary) params.set('summary', 'true');
    if (format) params.set('format', format);
    const qs = params.toString() ? `?${params}` : '';
    const data = await this.request(`/api/users/sessions${qs}`);
    return data.sessions || [];
  }

  async getSession(sessionId, { format } = {}) {
    const qs = format ? `?format=${encodeURIComponent(format)}` : '';
    return this.request(`/api/users/sessions/${encodeURIComponent(sessionId)}${qs}`);
  }

  async deleteSession(sessionId) {
    return this.request(`/api/users/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
  }

  async getSessionsByDevice(viUserId, { summary = false, format } = {}) {
    const id = viUserId || this.getViUserId();
    if (!id) return [];
    const params = new URLSearchParams({ vi_user_id: id });
    if (summary) params.set('summary', 'true');
    if (format) params.set('format', format);
    const data = await this.request(`/api/users/sessions/by-device?${params}`);
    return data.sessions || data || [];
  }

  // Memory endpoints

  /**
   * Check if current user is using device-based auth (no JWT).
   * Device auth users must use /by-device/ routes with vi_user_id param.
   */
  async _isDeviceAuth() {
    return !tokenStore.getAccess();
  }

  /** Build memory API path, switching to by-device route for device-auth users. */
  async _memoryPath(suffix) {
    if (await this._isDeviceAuth()) {
      const viUserId = this.getViUserId();
      const sep = suffix.includes('?') ? '&' : '?';
      return `/api/users/memory/by-device/${suffix}${viUserId ? `${sep}vi_user_id=${encodeURIComponent(viUserId)}` : ''}`;
    }
    return `/api/users/memory/${suffix}`;
  }

  /** Get structured memory — categories with items from pgvector. */
  async getStructuredMemory() {
    const path = await this._memoryPath('structured');
    return this.request(path);
  }

  async upsertMemory(filename, content) {
    // Encode each path segment individually to preserve slashes (e.g. "entities/mem_xxx.md")
    const safePath = filename.split('/').map(encodeURIComponent).join('/');
    const path = await this._memoryPath(safePath);
    return this.request(path, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  async deleteMemory(filename) {
    const safePath = filename.split('/').map(encodeURIComponent).join('/');
    const path = await this._memoryPath(safePath);
    return this.request(path, { method: 'DELETE' });
  }

  /** V8: Add a fact to a specific category. */
  async addFact(category, fact) {
    const path = await this._memoryPath('facts');
    return this.request(path, {
      method: 'POST',
      body: JSON.stringify({ category, fact }),
    });
  }

  /** V8: Delete a fact by UUID. */
  async deleteFact(factId) {
    const path = await this._memoryPath(`fact/${factId}`);
    return this.request(path, { method: 'DELETE' });
  }

  // S3 Upload endpoints
  async getPresignedUploadUrl(ext = 'jpg') {
    const params = new URLSearchParams({ ext });
    // Always attach vi_user_id for device-based auth fallback
    const viUserId = this.getViUserId();
    if (viUserId) params.set('vi_user_id', viUserId);
    return this.request(`/api/upload/presign?${params.toString()}`);
  }

  /**
   * Upload a blob to S3 via presigned URL with retry.
   * Returns the public S3 URL.
   *
   * Content-hash cache (issue #999): by default, a SHA-256 digest of the blob
   * is computed and looked up in `this.uploadCache`. On hit, the cached
   * public_url is returned immediately (zero network calls). On miss, the
   * result is written to the cache before returning. Pass { skipCache: true }
   * at call sites that upload always-novel content (e.g. live camera frames)
   * to avoid cache bloat from one-shot hashes.
   */
  async _uploadToS3(blob, ext, opts = {}) {
    const { skipCache = false, onCached } = opts;

    let hash = null;
    if (!skipCache && this.uploadCache) {
      try {
        hash = await hashBlob(blob);
        const hit = this.uploadCache.get(hash);
        if (hit && hit.public_url) {
          // Let callers distinguish "served from local content-hash cache"
          // from "round-tripped through S3" — the Intention page uses this
          // to flag instant uploads in the timing badge.
          try { onCached?.(); } catch (_) { /* never block upload */ }
          return hit.public_url;
        }
      } catch (err) {
        // Hashing failure (e.g. SubtleCrypto unavailable) — fall through to
        // the normal upload path rather than block the user.
        console.warn('[upload-cache] hash failed, bypassing cache:', err);
        hash = null;
      }
    }

    // Proxy upload: browser → api-server → S3 (avoids CORS issues with
    // direct browser-to-S3 PUT on local dev / restrictive networks).
    const viUserId = this.getViUserId();
    const qs = new URLSearchParams({ ext });
    if (viUserId) qs.set('vi_user_id', viUserId);
    const formData = new FormData();
    formData.append('file', blob, `upload.${ext}`);
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const result = await this.request(
          `/api/upload/proxy?${qs.toString()}`,
          { method: 'POST', body: formData, rawBody: true },
          { timeout: 30000, retries: 0 },
        );
        const public_url = result.public_url;
        if (hash && this.uploadCache) {
          this.uploadCache.set(hash, {
            public_url,
            ext,
            content_type: result.content_type,
            size: blob.size,
          });
        }
        return public_url;
      } catch (err) {
        lastErr = err;
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
      }
    }
    throw lastErr;
  }

  /**
   * Notify API server that an upload is complete.
   * This publishes a vi:media:{uid} event to Redis so NanoClaw can auto-trigger.
   */
  async _notifyUploadComplete(publicUrl, key, mediaType) {
    try {
      const viUserId = this.getViUserId();
      const qs = viUserId ? `?vi_user_id=${encodeURIComponent(viUserId)}` : '';
      await this.request(`/api/upload/complete${qs}`, {
        method: 'POST',
        body: JSON.stringify({
          key: key || '',
          media_type: mediaType,
          media_url: publicUrl,
        }),
      });
      // Upload complete notified
    } catch (err) {
      console.warn('[redis][frontend] Failed to notify upload complete:', err);
    }
  }

  async uploadDataUrl(dataUrl, opts = {}) {
    const resp = await fetch(dataUrl);
    const blob = await resp.blob();
    const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
    return this._uploadToS3(blob, ext, opts);
  }

  async uploadBlob(blob, ext = 'webm', opts = {}) {
    return this._uploadToS3(blob, ext, opts);
  }

  /**
   * Dispatch a task to NanoClaw via REST.
   * Use when LiveKit RPC is unavailable (e.g., skill tap without active session).
   * Results stream back via SSE.
   */
  async dispatchExec({ prompt, skillSlug, mediaUrls, sessionId, priority, params, context, model } = {}) {
    const viUserId = this.getViUserId();

    // Parse #skill-slug from prompt (e.g. "analyze this #nutrition-analyzer")
    if (prompt != null) prompt = String(prompt);
    if (!skillSlug && prompt) {
      const hashMatch = prompt.match(/#([a-zA-Z][a-zA-Z0-9_-]*)\s*$/);
      if (hashMatch) {
        skillSlug = hashMatch[1];
        prompt = prompt.slice(0, hashMatch.index).trim();
      }
    }

    const body = { prompt };
    if (skillSlug) body.skill_slug = skillSlug;
    if (mediaUrls) body.media_urls = mediaUrls;
    if (sessionId) body.session_id = sessionId;
    if (priority) body.priority = priority;
    if (params) body.params = params;
    if (context) body.context = context;
    if (model) body.model = model;

    if (!viUserId) throw new Error('viUserId is required for dispatchExec');
    // retries:0 — POST creates server-side session state, so a transparent
    // retry on timeout produces duplicate sessions. timeout:30000 — dispatch
    // can take >5s when nanoclaw is cold or the brief cache miss path runs.
    return this.request(
      `/api/users/exec?vi_user_id=${encodeURIComponent(viUserId)}`,
      { method: 'POST', body: JSON.stringify(body) },
      { timeout: 30000, retries: 0 },
    );
  }

  /**
   * Stop a running task via unified session stream endpoint.
   * @param {string} sessionId - The session containing the task
   * @param {string} taskId - The specific task to abort
   */
  async stopSession(sessionId, taskId) {
    return this.request(
      `/api/users/sessions/${encodeURIComponent(sessionId)}/stream`,
      {
        method: 'POST',
        body: JSON.stringify({ action: 'stop', data: { taskId } }),
      },
    );
  }

  /**
   * Inject user input into a running task via unified session stream endpoint.
   * The input is buffered and applied at the next SDK turn boundary.
   * @param {string} sessionId
   * @param {string} taskId - The active task to inject into
   * @param {string} text - User input text
   * @param {string[]} [mediaUrls] - Optional attachments composed into the injected turn
   */
  async injectInput(sessionId, taskId, text, mediaUrls) {
    const data = { taskId, text };
    if (Array.isArray(mediaUrls) && mediaUrls.length > 0) {
      data.mediaUrls = mediaUrls;
    }
    return this.request(
      `/api/users/sessions/${encodeURIComponent(sessionId)}/stream`,
      {
        method: 'POST',
        body: JSON.stringify({ action: 'inject', data }),
      },
    );
  }

  /**
   * Send a card action scoped to a specific session.
   * Uses the unified POST /sessions/{id}/stream endpoint — body-based routing
   * dispatches to card action handler when `action` field is present.
   */
  async sendSessionCardAction(sessionId, cardId, action, payload = {}) {
    return this.request(`/api/users/sessions/${encodeURIComponent(sessionId)}/stream`, {
      method: 'POST',
      body: JSON.stringify({ cardId, action, data: payload }),
    });
  }

  /**
   * Debug: get the assembled prompt that would be sent to Gemini for brief/stream.
   */
  async getBriefPrompt() {
    const viUserId = this.getViUserId();
    const qs = viUserId ? `?vi_user_id=${encodeURIComponent(viUserId)}` : '';
    return this.request(`/api/v1/session/brief/prompt${qs}`, {}, { timeout: 10000, retries: 0 });
  }

  /**
   * Fast session brief via Gemini Flash — returns summary + intention suggestions.
   * Used as the first step before any NanoClaw dispatch.
   */
  async sessionBrief(images = [], conversationSummary = null) {
    const body = {};
    if (images.length > 0) body.images = images;
    if (conversationSummary) body.conversation_summary = conversationSummary;
    return this.request('/api/v1/session/brief', {
      method: 'POST',
      body: JSON.stringify(body),
    }, { timeout: 30000, retries: 1 });
  }

  /**
   * Humanize technical agent steps into user-friendly emoji + text via Gemini Flash.
   * @param {Array<{label: string, activity: string}>} steps
   * @returns {Promise<{steps: Array<{emoji: string, text: string}>}>}
   */
  async humanizeSteps(steps) {
    return this.request('/api/v1/session/humanize-steps', {
      method: 'POST',
      body: JSON.stringify({ steps }),
    }, { timeout: 15000, retries: 0 });
  }

  /**
   * Get proactive task suggestions based on user memory.
   * @returns {Promise<{suggestions: Array<{icon: string, text: string}>}>}
   */
  async getSuggestions() {
    return this.request('/api/v1/session/suggestions', {
      method: 'GET',
    }, { timeout: 10000, retries: 1 });
  }

  /**
   * Streaming session brief — summary tokens arrive first, then intentions batch.
   * Returns an abort function to cancel the stream.
   *
   * Callbacks: { onSummaryToken(str), onIntentions([{icon,title,description}]), onComplete(), onError(err) }
   */
  streamSessionBrief(images, conversationSummary, { onSessionId, onSummaryToken, onSummaryDone, onIntentions, onMedia, onComplete, onError }) {
    // Issue #1015: generate a canonical UUID-with-dashes (36 chars). This
    // is the format the api-server DB column round-trips, the format
    // session_center.create_session returns, and the format the entire
    // /exec → cache → SSE chain uses. Langfuse derives its 32-hex OTel
    // trace_id server-side via uuid.UUID(session_id).hex.
    //
    // Same id, end-to-end: profile_log task_id, Langfuse trace_id, Redis
    // quick_response cache key, DB session row PK, frontend chat URL.
    const _hex = () => Math.floor(Math.random() * 16).toString(16);
    const _gen36 = () => {
      // Fallback when crypto.randomUUID isn't available (very old browsers).
      const h = Array.from({ length: 32 }, _hex).join('');
      return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-${'89ab'[Math.floor(Math.random() * 4)]}${h.slice(17, 20)}-${h.slice(20, 32)}`;
    };
    const sessionId = (globalThis.crypto?.randomUUID?.() || _gen36());
    const body = { session_id: sessionId };
    if (images?.length > 0) body.images = images.map(img => typeof img === 'object' ? img.url : img);
    if (conversationSummary) body.conversation_summary = conversationSummary;
    // Fire callback synchronously before awaiting fetch, so the UI can show
    // the id and the click handler can stash it for the dispatchExec call
    // that follows when the user taps an intention.
    try { onSessionId?.(sessionId); } catch (_) { /* callback errors are non-fatal */ }

    const viUserId = this.getViUserId();
    const qs = viUserId ? `?vi_user_id=${encodeURIComponent(viUserId)}` : '';

    const controller = new AbortController();

    (async () => {
      try {
        const authHeaders = await this.getAuthHeaders();
        const response = await fetch(`${this.baseUrl}/api/v1/session/brief/stream${qs}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              switch (payload.event) {
                case 'brief_session_id':
                  // Authoritative id from server — overrides client-side if
                  // server generated its own (e.g. if client omitted it).
                  if (payload.data?.session_id) onSessionId?.(payload.data.session_id);
                  break;
                case 'brief_summary_token':
                  onSummaryToken?.(payload.data.token);
                  break;
                case 'brief_summary_done':
                  onSummaryDone?.();
                  break;
                case 'brief_intentions':
                  onIntentions?.(payload.data.intentions);
                  break;
                case 'brief_media':
                  // Issue #1002: per-image descriptions + cache_hit + media_id.
                  // Shape: { media: [{ ref, media_id, description, cache_hit, error }] }
                  onMedia?.(payload.data.media || []);
                  break;
                case 'brief_complete':
                  onComplete?.();
                  break;
                case 'error':
                  onError?.(new Error(payload.data.message));
                  break;
              }
            } catch (parseErr) {
              // skip unparseable SSE lines
            }
          }
        }
        // onComplete is called via brief_complete SSE event above
      } catch (err) {
        if (err.name !== 'AbortError') onError?.(err);
      }
    })();

    return () => controller.abort();
  }

  // Cron & Heartbeat endpoints

  async getCronJobs() {
    const viUserId = this.getViUserId();
    const qs = viUserId ? `?vi_user_id=${encodeURIComponent(viUserId)}` : '';
    return this.request(`/api/users/cron${qs}`);
  }

  // Device endpoints
  async reportDevice(deviceInfo) {
    return this.request('/api/devices', {
      method: 'POST',
      body: JSON.stringify(deviceInfo),
    });
  }

  // OAuth token endpoints

  async getTokenStatus(provider) {
    return this.request(`/api/tokens/${encodeURIComponent(provider)}/status`);
  }

  async connectToken(provider) {
    return this.request(`/api/tokens/connect/${encodeURIComponent(provider)}`, { method: 'POST' });
  }

  async disconnectToken(provider) {
    return this.request(`/api/tokens/${encodeURIComponent(provider)}`, { method: 'DELETE' });
  }

  // Invite code endpoints

  async getInviteStatus() {
    return this.request('/api/invite/status');
  }

  async validateInviteCode(code) {
    return this.request(`/api/invite/validate?code=${encodeURIComponent(code)}`);
  }

  /**
   * Authenticated fetch — wraps native fetch() with Firebase auth headers.
   * Use this instead of bare fetch() for any /api/* endpoint.
   */
  async authFetch(url, options = {}) {
    const authHeaders = await this.getAuthHeaders();
    const mergedHeaders = { ...authHeaders, ...options.headers };
    return fetch(url, { ...options, headers: mergedHeaders });
  }

  /**
   * Persist a card's full state via replace_card in the nanoclaw WAL.
   * Single snapshot — on replay this IS the card. No incremental patches.
   * Goes through the first-class api-server route — no `/nanoclaw/` leak.
   */
  async updateSessionCard(sessionId, cardId, template, data) {
    try {
      const resp = await this.authFetch(
        `/api/sessions/${encodeURIComponent(sessionId)}/cards/${encodeURIComponent(cardId)}/update`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ template, data }),
        },
      );
      if (!resp.ok) console.warn('[api] updateSessionCard failed:', resp.status);
      return resp.ok ? await resp.json() : null;
    } catch (err) {
      console.warn('[api] updateSessionCard failed:', err.message || err);
    }
  }

  async getTemplateHtml(templateId) {
    // Try DB-backed templates first, fall back to NanoClaw proxy
    const resp = await this.authFetch(`/api/templates/${encodeURIComponent(templateId)}/html`);
    if (resp.ok) return resp.text();
    // Fallback: NanoClaw filesystem templates (legacy)
    const fallback = await this.authFetch(`/api/nanoclaw/dashboard/templates/${encodeURIComponent(templateId)}/html`);
    if (!fallback.ok) return null;
    return fallback.text();
  }

  async logout() {
    tokenStore.clear();
    this.setViUserId(null);
  }

  async deleteAccount() {
    return this.request('/api/users/me', { method: 'DELETE' });
  }
}

export const api = new ApiClient();

/** End-user login via username/password → JWT pair. */
export async function loginRequest(username, password) {
  const res = await fetch(`${api.baseUrl}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.detail?.message || `Login failed: ${res.status}`);
    err.code = data.detail?.code;
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  tokenStore.set(data.access_token, data.refresh_token);
  if (data.user?.vi_user_id) api.setViUserId(data.user.vi_user_id);
  return data;
}

/**
 * End-user self-service registration.
 *
 * Backend creates the user + mints a bound default API key in one transaction.
 * The plaintext key is returned **only once** — we immediately persist it in
 * localStorage so business calls can use ``sk-vi-*`` on the next request.
 */
export async function registerRequest({ email, password, displayName }) {
  const body = { email, password };
  if (displayName) body.display_name = displayName;
  const res = await fetch(`${api.baseUrl}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.detail?.message || `Register failed: ${res.status}`);
    err.code = data.detail?.code;
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  tokenStore.set(data.access_token, data.refresh_token);
  if (data.api_key?.plaintext) tokenStore.setApiKey(data.api_key.plaintext);
  if (data.user?.vi_user_id) api.setViUserId(data.user.vi_user_id);
  return data;
}

// ───────────────────────── /api/keys (user self-service) ─────────────────────

/** List the current user's bound API keys (plaintext NEVER returned). */
export async function keysListRequest() {
  return api.request('/api/keys', { method: 'GET' }, { preferApiKey: false, retries: 0 });
}

/** Mint a new bound API key. Response contains plaintext exactly once. */
export async function keysCreateRequest(name) {
  return api.request(
    '/api/keys',
    { method: 'POST', body: JSON.stringify({ name }) },
    { preferApiKey: false, retries: 0 },
  );
}

export async function keysUpdateRequest(id, { name, is_active } = {}) {
  const body = {};
  if (name !== undefined) body.name = name;
  if (is_active !== undefined) body.is_active = is_active;
  return api.request(
    `/api/keys/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    { preferApiKey: false, retries: 0 },
  );
}

export async function keysDeleteRequest(id) {
  return api.request(
    `/api/keys/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { preferApiKey: false, retries: 0 },
  );
}

// ───────────────────────── /api/cards ─────────────────────────────────────────

export async function cardsListRequest() {
  return api.request('/api/cards', { method: 'GET' }, { preferApiKey: false, retries: 0 });
}

export async function cardsCreateRequest(card) {
  return api.request(
    '/api/cards',
    { method: 'POST', body: JSON.stringify(card) },
    { preferApiKey: false, retries: 0 },
  );
}

export async function cardsUpdateRequest(id, patch) {
  return api.request(
    `/api/cards/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    { preferApiKey: false, retries: 0 },
  );
}

export async function cardsDeleteRequest(id) {
  return api.request(
    `/api/cards/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { preferApiKey: false, retries: 0 },
  );
}

/** Refresh access token using the stored refresh token. */
export async function refreshRequest() {
  const refresh = tokenStore.getRefresh();
  if (!refresh) throw new Error('no_refresh_token');
  const res = await fetch(`${api.baseUrl}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  if (!res.ok) {
    tokenStore.clear();
    throw new Error(`Refresh failed: ${res.status}`);
  }
  const data = await res.json();
  tokenStore.setAccess(data.access_token);
  return data.access_token;
}

/** Fetch current user profile using the stored access token. */
export async function meRequest() {
  return api.request('/api/auth/me');
}

/** Clear local tokens (no server call). */
export function logoutLocal() {
  tokenStore.clear();
  api.setViUserId(null);
}

// Dev helper: inspect / clear the upload cache from the browser console.
// Gated to dev builds only — ships nothing to prod bundles.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__uploadCache = {
    list: () => api.uploadCache.list(),
    size: () => api.uploadCache.size(),
    clear: () => api.uploadCache.clear(),
  };
}
